import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import getConnection from "core/shared/util/marvellConnection";
import ReportsConstants from "reports/utils/ReportsConstants";
import ReportsMessages from "reports/intl/ReportsMessages";
import { triggerCSVDownload } from "reports/shared/utils/util";

const { EXPORT_COOLDOWN_MS } = ReportsConstants;

// In-memory store for download timestamps
const downloadTimestamps = {};

// Export for testing purposes only
// eslint-disable-next-line no-underscore-dangle
export const __clearDownloadTimestamps = () => {
  Object.keys(downloadTimestamps).forEach((key) => {
    delete downloadTimestamps[key];
  });
};

const canDownload = (id) => {
  if (!downloadTimestamps[id]) {
    return true;
  }

  const now = new Date().getTime();
  const timeSinceLastDownload = now - downloadTimestamps[id];

  return timeSinceLastDownload >= EXPORT_COOLDOWN_MS;
};

const generateDownloadToken = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

const transformSending = (reportInfo) => {
  const {
    name,
    report,
    definition: {
      format,
      filter,
      dimensions,
      metrics,
      startDate,
      timeWindow,
      timeRollup,
      v1,
    },
  } = reportInfo;

  return {
    reportType: report,
    downloadToken: generateDownloadToken(),
    filename: `${name}.${format.toLowerCase()}`,
    format,
    filter,
    dimensions,
    metrics,
    startDate,
    timeWindow,
    timeRollup,
    v1,
  };
};

/**
 * Hook for downloading/exporting data reports
 * Handles single or multiple report exports with cooldown, error handling, and user feedback
 *
 * @param {Object} toastRef - Optional React ref to Toast component for notifications
 * @returns {Object} - { isSending, postData, handleExport }
 */
const useDownloadDataReport = (toastRef = null) => {
  const connection = getConnection();
  const abortControllerRef = useRef();
  const [isSending, setIsSending] = useState(false);
  const { formatMessage } = useIntl();

  const postData = useCallback(
    async ({ reportInfo }) => {
      const { id } = reportInfo;

      // Check if download is allowed based on cooldown period
      if (!canDownload(id)) {
        const now = new Date().getTime();
        const remainingTime = Math.ceil(
          (EXPORT_COOLDOWN_MS - (now - downloadTimestamps[id])) / 1000
        );
        return {
          error: "Download cooldown active",
          remainingTime,
        };
      }

      const params = transformSending(reportInfo);
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      setIsSending(true);

      try {
        const response = await connection.exportDataReport({
          signal,
          params,
        });

        // Update timestamp on successful download to reset cooldown
        downloadTimestamps[id] = new Date().getTime();

        setIsSending(false);
        return response;
      } catch (err) {
        setIsSending(false);

        // Reset cooldown on error to allow immediate retry
        delete downloadTimestamps[id];

        throw err;
      }
    },
    [connection]
  );

  const handleExport = useCallback(
    async (reportInfoOrList) => {
      // Handle single report or array of reports
      const reports = Array.isArray(reportInfoOrList)
        ? reportInfoOrList
        : [reportInfoOrList];

      if (!reports || reports.length === 0) {
        toastRef?.current?.show({
          detail: formatMessage(ReportsMessages.noReportsSelected),
          severity: "warn",
          summary: formatMessage(ReportsMessages.downloadError),
          life: 3000,
        });
        return;
      }

      let successCount = 0;
      let failedCount = 0;
      let cooldownCount = 0;

      // Process each report
      const results = await Promise.all(
        reports.map(async (reportInfo) => {
          try {
            const response = await postData({ reportInfo });

            // Check if response contains error (cooldown active)
            if (response?.error) {
              return {
                status: "cooldown",
                remainingTime: response.remainingTime,
              };
            }

            // Trigger CSV download with response data
            if (response?.data) {
              const { name, definition } = reportInfo;
              const format = definition?.format || "csv";
              const filename = `${name}.${format.toLowerCase()}`;

              triggerCSVDownload(response.data, filename);
              return { status: "success" };
            }

            return { status: "failed" };
          } catch (error) {
            return { status: "failed", error };
          }
        })
      );

      // Count results
      results.forEach((result) => {
        if (result.status === "success") {
          successCount += 1;
        } else if (result.status === "cooldown") {
          cooldownCount += 1;
        } else if (result.status === "failed") {
          failedCount += 1;
        }
      });

      // Show single report error messages
      if (reports.length === 1) {
        const result = results[0];
        if (result.status === "cooldown") {
          toastRef?.current?.show({
            detail: formatMessage(ReportsMessages.downloadCooldownError, {
              remainingTime: result.remainingTime,
            }),
            severity: "warn",
            summary: formatMessage(ReportsMessages.downloadError),
            life: 5000,
          });
          return;
        }
        if (result.status === "failed") {
          toastRef?.current?.show({
            detail: result.error?.message || "An unexpected error occurred.",
            severity: "error",
            summary: formatMessage(ReportsMessages.downloadError),
            life: 5000,
          });
          return;
        }
      }

      // Show summary message for multiple reports
      if (reports.length > 1) {
        if (successCount > 0) {
          toastRef?.current?.show({
            detail: formatMessage(ReportsMessages.bulkDownloadSuccess, {
              count: successCount,
            }),
            severity: "success",
            summary: formatMessage(ReportsMessages.toastSuccess),
            life: 3000,
          });
        }

        if (failedCount > 0 || cooldownCount > 0) {
          const message = [];
          if (failedCount > 0) {
            message.push(
              formatMessage(ReportsMessages.bulkDownloadFailed, {
                count: failedCount,
              })
            );
          }
          if (cooldownCount > 0) {
            message.push(
              formatMessage(ReportsMessages.bulkDownloadCooldown, {
                count: cooldownCount,
              })
            );
          }

          toastRef?.current?.show({
            detail: message.join(" "),
            severity: "warn",
            summary: formatMessage(ReportsMessages.downloadError),
            life: 5000,
          });
        }
      } else if (successCount === 1) {
        // Show success message for single report
        toastRef?.current?.show({
          detail: formatMessage(ReportsMessages.downloadSuccess),
          severity: "success",
          summary: formatMessage(ReportsMessages.toastSuccess),
          life: 3000,
        });
      }
    },
    [postData, formatMessage, toastRef]
  );

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return { isSending, postData, handleExport };
};

export default useDownloadDataReport;
